import React, { useState, useContext } from "react";
import { useFirestore } from "../../firebase/FirestoreContext";
import { doc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { EnvContext } from "../../../context/EnvContext";
import config from "../../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const NewShippingOption = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const navigate = useNavigate();

  const collectionPath =
    environment === "staging"
      ? config.enveironment.staging.collectionPath
      : config.enveironment.production.collectionPath;

  const [shippingData, setShippingData] = useState({
    name: "",
    slug: "",
    minimum_days: "",
    maximum_days: "",
    free_threshold: "",
    price: "",
  });
  const [loading, setLoading] = useState(false);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setShippingData({
      ...shippingData,
      name: newName,
      slug: generateSlug(newName),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!shippingData.name || !shippingData.minimum_days || !shippingData.maximum_days || !shippingData.price) {
      toast.error("Please fill in all required fields");
      setLoading(false);
      return;
    }

    if (!shippingData.slug || shippingData.slug.trim() === "") {
      toast.error("Slug is required");
      setLoading(false);
      return;
    }

    const shippingCollectionRef = collection(
      db,
      `${collectionPath}ShippingOptions/ShippingOptionsList/DataBase`
    );

    try {
      const slugQuery = query(
        shippingCollectionRef,
        where("slug", "==", shippingData.slug)
      );
      const slugSnapshot = await getDocs(slugQuery);

      if (!slugSnapshot.empty) {
        toast.error("This slug already exists. Please use a different slug.");
        setLoading(false);
        return;
      }

      const newShippingOption = {
        name: shippingData.name,
        slug: shippingData.slug,
        minimum_days: parseInt(shippingData.minimum_days),
        maximum_days: parseInt(shippingData.maximum_days),
        free_threshold: parseFloat(shippingData.free_threshold) || 0,
        price: parseFloat(shippingData.price),
      };

      const newDocRef = doc(shippingCollectionRef);
      await setDoc(newDocRef, newShippingOption);

      toast.success("Shipping option created successfully!");
      setTimeout(() => {
        navigate(`/main/shipping-options`);
      }, 1000);
    } catch (error) {
      console.error("Transaction failed:", error);
      toast.error("Error creating shipping option. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    form: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      padding: 20,
      border: "1px solid #ccc",
      borderRadius: 5,
      backgroundColor: "#f9f9f9",
      maxWidth: "600px",
    },
    formElement: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
      marginBottom: 10,
    },
    formLabel: {
      marginBottom: 5,
      fontSize: 12,
      textTransform: "uppercase",
      color: "#4a4141",
      fontWeight: "bold",
    },
    formInput: {
      padding: "10px 15px",
      marginBottom: 10,
      fontSize: 16,
      border: ".5px solid #ccc",
      borderRadius: 5,
    },
    formInputDisabled: {
      padding: "10px 15px",
      marginBottom: 10,
      fontSize: 16,
      border: ".5px solid #ccc",
      borderRadius: 5,
      backgroundColor: "#e9ecef",
      color: "#6c757d",
    },
    button: {
      padding: "12px 24px",
      backgroundColor: "#478169",
      color: "white",
      border: "none",
      borderRadius: 5,
      cursor: "pointer",
      fontSize: "16px",
      marginTop: "10px",
      transition: "background-color 0.3s",
    },
    buttonDisabled: {
      padding: "12px 24px",
      backgroundColor: "#cccccc",
      color: "#666666",
      border: "none",
      borderRadius: 5,
      cursor: "not-allowed",
      fontSize: "16px",
      marginTop: "10px",
    },
    helpText: {
      fontSize: 12,
      color: "#6c757d",
      marginTop: -8,
      marginBottom: 10,
    },
  };

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />
      <h2>Create New Shipping Option</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formElement}>
          <label style={styles.formLabel}>Name: *</label>
          <input
            type="text"
            value={shippingData.name}
            onChange={handleNameChange}
            style={styles.formInput}
            required
          />
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Slug: *</label>
          <input
            type="text"
            value={shippingData.slug}
            onChange={(e) =>
              setShippingData({ ...shippingData, slug: e.target.value })
            }
            style={styles.formInput}
            required
          />
          <div style={styles.helpText}>
            URL-friendly identifier (auto-generated from name but can be edited)
          </div>
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Minimum Days: *</label>
          <input
            type="number"
            min="0"
            value={shippingData.minimum_days}
            onChange={(e) =>
              setShippingData({ ...shippingData, minimum_days: e.target.value })
            }
            style={styles.formInput}
            required
          />
          <div style={styles.helpText}>
            Minimum number of days for delivery
          </div>
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Maximum Days: *</label>
          <input
            type="number"
            min="0"
            value={shippingData.maximum_days}
            onChange={(e) =>
              setShippingData({ ...shippingData, maximum_days: e.target.value })
            }
            style={styles.formInput}
            required
          />
          <div style={styles.helpText}>
            Maximum number of days for delivery
          </div>
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Free Threshold:</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={shippingData.free_threshold}
            onChange={(e) =>
              setShippingData({
                ...shippingData,
                free_threshold: e.target.value,
              })
            }
            style={styles.formInput}
          />
          <div style={styles.helpText}>
            Minimum order amount for free shipping (leave 0 for no free shipping)
          </div>
        </div>

        <div style={styles.formElement}>
          <label style={styles.formLabel}>Price: *</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={shippingData.price}
            onChange={(e) =>
              setShippingData({ ...shippingData, price: e.target.value })
            }
            style={styles.formInput}
            required
          />
          <div style={styles.helpText}>
            Shipping price in dollars
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          style={loading ? styles.buttonDisabled : styles.button}
        >
          {loading ? "Creating..." : "Create Shipping Option"}
        </button>
      </form>
    </div>
  );
};

export default NewShippingOption;
